import { ReactElement } from 'react';

import { Money } from '@dreryk/money-common-api';
import {
   AppointmentAdvancePaymentRefundCommand,
   CannotRefundManualPaymentFault,
   RefundAmountExceedsRemainingAdvancePaymentAmountFault,
} from '@dreryk/egabinet-appointment-advance-payment-frontend-api';

import { moneySubtract } from '@dreryk/react-common';
import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';

import { RefundAdvancePaymentFormBean } from '../../form/RefundAdvancePaymentForm.types';
import { refundAdvancePaymentTranslationKeys } from '../../translations';
import { OnRefundSuccessCallback } from '../RefundAdvancePaymentShowModalEventInterceptor.types';

const t = refundAdvancePaymentTranslationKeys.modal.actions.confirm;

type AppointmentAdvancePaymentRefundActionRenderProps = {
   refundAppointmentAdvancePayment: (formData: RefundAdvancePaymentFormBean) => Promise<void>;
};

type AppointmentAdvancePaymentRefundActionProps = {
   advancePaymentId: number;
   advancePaymentTransactionId: string;
   children: (renderProps: AppointmentAdvancePaymentRefundActionRenderProps) => ReactElement;
   onSuccess: OnRefundSuccessCallback;
};

export const AppointmentAdvancePaymentRefundAction = (
   props: AppointmentAdvancePaymentRefundActionProps
): ReactElement => {
   const { executeAsync } = useCqrsCommand<
      void,
      RefundAdvancePaymentFormBean,
      CannotRefundManualPaymentFault | RefundAmountExceedsRemainingAdvancePaymentAmountFault
   >(
      (parameters) => {
         const command = new AppointmentAdvancePaymentRefundCommand();
         command.amount = Money.of(parameters.refundAmount);
         command.appointmentAdvancePaymentId = Number(props.advancePaymentTransactionId);
         return command;
      },
      {
         onSuccess: (__, parameters) => {
            dispatchSnackbarPositiveEvent(t.successMessage);
            const remainingAmount = moneySubtract(parameters.advancePaymentAmount, parameters.refundAmount);
            props.onSuccess(remainingAmount, props.advancePaymentId);
         },
         onFault: {
            'egabinet-appointment-advance-payment-frontend-api:CannotRefundManualPaymentFault': () =>
               dispatchSnackbarNegativeEvent(t.failureMessage),
            'egabinet-appointment-advance-payment-frontend-api:RefundAmountExceedsRemainingAdvancePaymentAmountFault': () =>
               dispatchSnackbarNegativeEvent(t.failureMessage),
         },
      }
   );

   return props.children({ refundAppointmentAdvancePayment: executeAsync });
};
